import React, { useEffect } from 'react';
import { css, useTheme } from '@emotion/react';
import styled from 'types/theme.types';
import { Menu } from 'antd';
import ScrollIntoViewIfNeeded from 'react-scroll-into-view-if-needed';

import type { MenuProps as AntdMenuProps } from 'antd/lib/menu';
import { legacyOnly, newDesignOnly, type Env0Theme } from 'constants/themes.constants';
import Tabs from 'components/common/tabs';

import { useNavigate, useLocation } from 'react-router-dom';

type BaseMenuProps = {
  setActiveMenu: React.Dispatch<string>;
  activeMenu: string;
  menus: readonly string[];
  children: React.ReactNode;
  renderMenuItem?: (key: string) => React.ReactNode;
};

/**
 * `compact` is the former menu2: a shorter bar, no url sync, no scroll anchor, no custom actions.
 * It exists so the switch-off render of its three call sites stays pixel-identical; the new design
 * collapses both variants onto one treatment, and APO-484 deletes the prop.
 *
 * A union rather than one optional bag: the compact branch renders no `customActions`, so accepting
 * the prop there would swallow it silently — `never` turns that into a compile error.
 */
export type MenuProps = BaseMenuProps &
  ({ variant?: 'page'; customActions?: React.ReactNode } | { variant: 'compact'; customActions?: never });

export const TAB_SEARCH_PARAM_NAME = 'tabname';
export const TAB_ANCHOR_HASH = '#tab';

/**
 * The page tab bar. Behaviour is one code path for both designs; only the bar itself branches — the
 * new design renders Nav / Tab through `components/common/tabs`, legacy keeps the antd Menu bar it
 * always rendered, byte-compared by `menu-legacy-parity.spec`.
 */
const TabsMenu: React.FunctionComponent<MenuProps> = ({
  activeMenu,
  setActiveMenu,
  menus,
  children,
  renderMenuItem,
  customActions,
  variant = 'page'
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const syncsWithUrl = variant === 'page';

  useEffect(() => {
    if (!syncsWithUrl) return;

    const searchParams = new URLSearchParams(location.search);

    if (searchParams.has(TAB_SEARCH_PARAM_NAME)) {
      const tab = searchParams.get(TAB_SEARCH_PARAM_NAME);

      if (tab && menus.includes(tab)) {
        setActiveMenu(tab);
      }
    }
  }, [location, setActiveMenu, menus, syncsWithUrl]);

  const onTabChange = (key: string) => {
    if (syncsWithUrl) {
      const searchParams = new URLSearchParams(location.search);

      searchParams.set(TAB_SEARCH_PARAM_NAME, key);
      location.search = searchParams.toString();
      navigate(location);
    }

    setActiveMenu(key);
  };

  const onMenuClick: AntdMenuProps['onClick'] = event => onTabChange(event.key.toString());

  // The legacy style is applied inline because the Menu adjusts the items inside a dropdown in Tablet
  // and smaller viewports and the Styled CSS is not applied to those items.
  const renderMenuItemFunc =
    renderMenuItem ??
    (theme.isNewDesign
      ? (menu: string) => menu
      : (menu: string) => <div style={{ textTransform: 'uppercase' }}>{menu}</div>);

  const menuProps = {
    onClick: onMenuClick,
    selectedKeys: [activeMenu],
    mode: 'horizontal' as const,
    items: menus.map(menuKey => ({
      key: menuKey,
      label: renderMenuItemFunc(menuKey),
      'data-e2e': `menu-${menuKey}`
    }))
  };

  const tabsProps = {
    activeKey: activeMenu,
    onChange: onTabChange,
    items: menus.map(menuKey => ({
      key: menuKey,
      // A div, not a span: ::first-letter sentence-casing needs a block container, and the e2e hook
      // the Menu bar carried on its items (`menu-<key>`, clicked by cypress) has to survive the swap.
      label: <div data-e2e={`menu-${menuKey}`}>{renderMenuItemFunc(menuKey)}</div>
    }))
  };

  const bar = theme.isNewDesign ? (
    syncsWithUrl ? (
      <PageBarTabs {...tabsProps} />
    ) : (
      <CompactBarTabs {...tabsProps} />
    )
  ) : syncsWithUrl ? (
    <PageMenu {...menuProps} />
  ) : (
    <CompactMenu {...menuProps} />
  );

  return (
    <Container data-e2e={syncsWithUrl ? undefined : 'tabs-menu'}>
      {syncsWithUrl ? (
        <>
          <ScrollIntoViewIfNeeded
            active={location.hash === TAB_ANCHOR_HASH}
            options={{ behavior: 'smooth', block: 'start', scrollMode: 'always' }}
          />
          <MenuContainer>
            {bar}
            {customActions}
          </MenuContainer>
        </>
      ) : (
        // No wrapper on purpose: in menu2 the bar sat directly inside this block container,
        // where its `flex: 1` is inert. A flex wrapper would activate it and move the layout.
        bar
      )}
      <ContentContainer>{children}</ContentContainer>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const MenuContainer = styled.div`
  display: flex;

  /* The legacy bar hangs its 47px row off the bottom rule; the new design's 32px row centres on it.
     Branched rather than switched outright - the switch-off render is byte-compared. */
  ${legacyOnly(css`
    align-items: flex-end;
  `)}
  ${newDesignOnly(css`
    align-items: center;
  `)}
  justify-content: space-between;

  ${legacyOnly(
    theme => css`
      border-bottom: 2px solid ${theme.menuBorder} !important;
      height: 47px;
    `
  )}

  ${newDesignOnly(
    theme => css`
      border-bottom: ${theme.borderWidth.xs} solid var(--border-primary);
      height: 32px;
    `
  )}
`;

const ContentContainer = styled.div`
  ${legacyOnly(css`
    padding: 18px 0;
  `)}
  /* Figma's Main content container sits 12px under the tab bar (267:6868, gap-12). */
  ${newDesignOnly(css`
    padding: var(--spacing-md) 0;
  `)}
`;

/* The bar renders no panes — TabsMenu owns the content slot — but antd mounts a pane holder even for
   childless items, and an empty holder still takes part in layout. */
const barOnly = css`
  min-width: 0;

  .ant-tabs-content-holder {
    display: none;
  }
`;

const PageBarTabs = styled(Tabs)`
  ${barOnly}
  flex: 1;

  /* MenuContainer owns the bar rule for the page variant, so it runs under customActions too;
     a second rule on the nav would double it. */
  .ant-tabs-nav::before {
    border-bottom: none;
  }
`;

const CompactBarTabs = styled(Tabs)`
  ${barOnly}
`;

const legacyItem = (theme: Env0Theme) => css`
  color: ${theme.menuItemText} !important;
  border-bottom-color: transparent;
  font-size: 15px;
  font-weight: 600;
  line-height: 31px;
  letter-spacing: -0.08px;
  text-transform: uppercase;
  z-index: 2;

  &-selected:after,
  &:hover:after {
    color: ${theme.primaryBlack} !important;
    border-bottom-width: 3px !important;
    border-bottom-color: ${theme.primaryBlack} !important;
  }
`;

// Two styled components rather than one taking a variant prop: emotion forwards every prop to the
// component it wraps, so a `$variant` would land on antd's Menu and from there on the DOM. Both are
// legacy-only render paths now — the new design's bar is PageBarTabs / CompactBarTabs above.
const PageMenu = styled(Menu)`
  ${legacyOnly(
    theme => css`
      background: transparent !important;
      border-bottom-width: 0 !important;
      margin-bottom: -1px;
      flex: 1;
      overflow: hidden;

      .ant-menu-item {
        border-bottom-width: 4px !important;
        ${legacyItem(theme)}
      }
    `
  )}
`;

const CompactMenu = styled(Menu)`
  ${legacyOnly(
    theme => css`
      background: transparent !important;
      border-bottom: 2px solid ${theme.menuBorder} !important;
      height: 35px;
      flex: 1;

      .ant-menu-item {
        border-bottom-width: 3px !important;
        padding: 0 22px !important;
        ${legacyItem(theme)}
      }
    `
  )}
`;

export default TabsMenu;
